import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@marine/api-client/api';

/**
 * Platform-managed DEFAULT AI prompt templates (#867) — the landlord-DB rows
 * every dealership inherits until it customizes its own tenant `ai_prompts`
 * row. Field names mirror the tenant `AIPrompt` type
 * (`@/modules/core/types/aiPrompt.types` in apps/tenant) so the two editors
 * stay comparable.
 */
export interface PlatformAIPrompt {
  id: string;
  module: string;
  key: string;
  name: string;
  system_prompt: string;
  user_prompt_template: string;
  model_override: string | null;
  temperature_override: number | null;
  max_tokens_override: number | null;
  is_active: boolean;
  sort_order: number;
  description: string | null;
  variables: string[] | null;
  metadata: { category?: string; [key: string]: unknown } | null;
  updated_at: string | null;
  updated_by: string | null;
}

export interface UpdatePlatformAIPromptInput {
  system_prompt?: string;
  user_prompt_template?: string;
  model_override?: string | null;
  temperature_override?: number | null;
  max_tokens_override?: number | null;
  is_active?: boolean;
}

// `api.getList` / `api.putOne` already unwrap the `{data: ...}` envelope, so
// these signatures and the hooks below DO NOT need a `select` step.
export const platformAiPromptsApi = {
  list: (module?: string): Promise<PlatformAIPrompt[]> => {
    const params = module ? `?module=${module}` : '';

    return api.getList<PlatformAIPrompt>(`/api/platform/ai-prompts${params}`);
  },
  update: (id: string, input: UpdatePlatformAIPromptInput): Promise<PlatformAIPrompt> =>
    api.putOne<PlatformAIPrompt>(`/api/platform/ai-prompts/${id}`, input),
};

export function usePlatformAIPrompts(module?: string) {
  return useQuery({
    queryKey: ['platform-ai-prompts', module],
    queryFn: () => platformAiPromptsApi.list(module),
  });
}

export function useUpdatePlatformAIPrompt() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdatePlatformAIPromptInput }) =>
      platformAiPromptsApi.update(id, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['platform-ai-prompts'] });
    },
  });
}
